import { useState, useRef, useEffect } from "react";
import { X, ChevronDown } from "lucide-react";
import { cn } from "../lib/utils";

interface FlatCategory {
  id: number;
  label: string;
}

interface MultiCategoryPickerProps {
  categories: FlatCategory[];
  selected: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

export function MultiCategoryPicker({
  categories,
  selected,
  onChange,
  disabled,
}: MultiCategoryPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const filtered = search
    ? categories.filter((c) => c.label.toLowerCase().includes(search.toLowerCase()))
    : categories;

  const selectedCats = categories.filter((c) => selected.includes(c.id));

  function toggle(id: number) {
    onChange(
      selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id],
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "input cluster",
          "hover:border-coral/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          disabled && "opacity-50 cursor-not-allowed",
        )}
        style={{ minHeight: "var(--tap-mobile)", height: "auto", padding: ".375rem .75rem", textAlign: "left" }}
      >
        {selectedCats.length === 0 ? (
          <span className="text-md text-ink-2">Selecionar categorias...</span>
        ) : (
          selectedCats.map((c) => (
            <span key={c.id} className="chip chip--coral">
              {c.label}
              {!disabled && (
                <X
                  className="icon"
                  style={{ width: ".75rem", height: ".75rem" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(c.id);
                  }}
                />
              )}
            </span>
          ))
        )}
        <ChevronDown className="icon text-ink-3" style={{ marginLeft: "auto", flexShrink: 0 }} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-line bg-card shadow-elevated">
          <div style={{ padding: "var(--sp-2)", borderBottom: "var(--border)" }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar categoria..."
              className="input"
              style={{ border: "none", background: "transparent", minHeight: "auto", padding: 0 }}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto" style={{ padding: "var(--sp-1)" }}>
            {filtered.length === 0 ? (
              <div className="field__hint" style={{ padding: "var(--sp-3) var(--sp-2)", textAlign: "center" }}>
                Nenhuma categoria encontrada
              </div>
            ) : (
              filtered.map((c) => (
                <label
                  key={c.id}
                  className="cluster text-md text-ink hover:bg-soft"
                  style={{ borderRadius: "var(--radius-sm)", padding: ".375rem .5rem", cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(c.id)}
                    onChange={() => toggle(c.id)}
                    className="accent-coral"
                  />
                  {c.label}
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
